import React from 'react';
import type { IEnvironment } from 'relay-runtime';

import { fg } from '@confluence/feature-gating';

import { ForgeMacroMeasurements } from './ForgeMacroMeasurements.tsx';
import { ForgeMacro, getForgeMacroNodeProps } from './ForgeMacro.tsx';
import {
	type PreloadedStaticMacro,
	readPreloadedStaticMacros,
} from './readPreloadedStaticMacros.ts';

const FORGE_EXTENSION_TYPE = 'com.atlassian.ecosystem';

type PreloadedStaticMacroHandler = (extension: any) => React.ReactElement | null;

/**
 * Renders SSR-preloaded Forge static macro HTML synchronously, instead of waiting for the Forge
 * extension provider's manifest. Returns `null` for macros without preloaded HTML so the renderer
 * falls back to the extension provider as before.
 */
export function createPreloadedStaticMacroHandlers({
	contentId,
	cloudId,
	relayEnvironment,
}: {
	contentId: string;
	cloudId?: string;
	relayEnvironment: IEnvironment | undefined;
}): Record<string, PreloadedStaticMacroHandler> | undefined {
	// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
	if (!relayEnvironment || !fg('conf_frontend_static_macro_perf')) {
		return undefined;
	}

	let preloaded: Map<string, PreloadedStaticMacro> | undefined;

	const handler: PreloadedStaticMacroHandler = (extension) => {
		const nodeProps = getForgeMacroNodeProps(extension);
		if (!nodeProps.localId) {
			return null;
		}

		preloaded ??= readPreloadedStaticMacros(relayEnvironment, contentId, cloudId);
		const preloadedHtml = preloaded.get(nodeProps.localId)?.value;
		if (!preloadedHtml) {
			return null;
		}

		const macro = (
			<ForgeMacro
				{...nodeProps}
				preloadedHtml={preloadedHtml}
				// No access to the provider's iframe renderer here; the preloaded HTML is final for the view page.
				renderIframe={() => (
					<div
						data-testid="forge-static-macro-content"
						// eslint-disable-next-line react/no-danger
						dangerouslySetInnerHTML={{ __html: preloadedHtml }}
					/>
				)}
			/>
		);
		return (
			<ForgeMacroMeasurements extensionKey={extension.extensionKey} nodeType={extension.type}>
				{macro}
			</ForgeMacroMeasurements>
		);
	};

	return { [FORGE_EXTENSION_TYPE]: handler };
}
